import React, { useCallback } from 'react';

import moment from 'moment';

import { Month } from './styles';

interface Props {
  allDate?: boolean;
  year: number;
  month: number;
  initialDate?: Date;
  finalDate?: Date;
  noWeek?: boolean;
  onlyDate?: boolean;
  className?: string;
  minRange?: number;
  onChange: (date: Date) => void;
}

const MonthPicker = ({
  year,
  allDate,
  month,
  initialDate,
  finalDate,
  onlyDate,
  onChange,
  className,
  minRange,
}: Props) => {
  const isOutdatedDay = useCallback(
    (day: number) => {
      let dateWithMinRange;
      let dateWithOutMinRange;
      const date = moment({ year, month, day, hour: 0 });
      const now = moment({ hour: 0 });

      if (minRange) {
        dateWithMinRange = moment(initialDate).add(minRange - 1, 'days');
        dateWithOutMinRange = moment(finalDate).subtract(minRange - 1, 'days');
      }

      return (
        (minRange && initialDate && date.isSameOrAfter(initialDate) && date.isSameOrBefore(dateWithMinRange)) ||
        (minRange && finalDate && date.isSameOrBefore(finalDate) && date.isSameOrAfter(dateWithOutMinRange)) ||
        (!allDate && date.isBefore(now)) ||
        date.isSameOrAfter(now.add(1, 'y'))
      );
    },
    [year, month, initialDate, finalDate, allDate, minRange],
  );

  const formatToTableDaysArray = useCallback((initialDayInWeek: number, days: number) => {
    const arr = [...initialDayInWeek.toArray().map(() => 'before'), ...days.toArray()];
    const rowsSize = Math.ceil(arr.length / 7);
    const rowsAndColumns = rowsSize.toArray().map(() => arr.splice(0, 7));
    return rowsAndColumns.map((row) =>
      row.length < 7 ? [...row, ...(7 - row.length).toArray().map(() => 'after')] : row,
    );
  }, []);

  const getMonthDayClass = useCallback(
    (day: number | string) => {
      if (typeof day !== 'number') return '';

      const date = moment({ year, month, day });

      if (day && (date.isSame(initialDate) || date.isSame(finalDate))) {
        if (initialDate && finalDate && !moment(initialDate).isSame(finalDate)) {
          return `active ${date.isSame(initialDate) ? 'active-initial' : 'active-final'}`;
        }
        return 'active';
      }

      if (!onlyDate && initialDate && finalDate && date.isAfter(initialDate) && date.isSameOrBefore(finalDate)) {
        return 'in-range';
      }

      if (date.isSame(moment({ hour: 0 }))) {
        return 'today';
      }

      if (isOutdatedDay(day)) {
        return 'outdated';
      }

      return '';
    },
    [initialDate, finalDate, year, month, onlyDate, isOutdatedDay],
  );

  const getMonthDayTestId = (day: string | number) => {
    if (typeof day !== 'number') return '';

    const TODAY = moment({ hour: 0 });
    const TOMORROW = moment({ hour: 0 }).add(1, 'days');
    const NEXT_WEEK = moment({ hour: 0 }).add(1, 'weeks');
    const OTHER_WEEK = moment({ hour: 0 }).add(2, 'weeks');

    const date = moment({ year, month, day });

    if (date.isSame(TODAY)) return 'calendar-day-today';
    if (date.isSame(TOMORROW)) return 'calendar-day-tomorrow';
    if (date.isSame(NEXT_WEEK)) return 'calendar-day-next-week';
    if (date.isSame(OTHER_WEEK)) return 'calendar-day-other-week';
  };

  const handleClickMonthDay = (day: number) => {
    if (!isOutdatedDay(day)) {
      onChange(moment({ year, month, day }).toDate());
    }
  };

  const renderMonth = () => {
    const days = moment({ year, month }).daysInMonth();
    const initialDayInWeek = moment({ year, month, day: 1 }).weekday();

    const daysInTable = formatToTableDaysArray(initialDayInWeek, days);

    return (
      <Month>
        {daysInTable.map((rows, rowIndex) => (
          <Month.Week key={rowIndex}>
            {rows.map((day, dayIndex) => (
              <Month.Week.Day
                key={dayIndex}
                onClick={typeof day === 'number' ? () => handleClickMonthDay(day) : null}
                className={getMonthDayClass(day)}
                data-testid={getMonthDayTestId(day)}
              >
                <span>{typeof day === 'number' ? day : null}</span>
              </Month.Week.Day>
            ))}
          </Month.Week>
        ))}
      </Month>
    );
  };

  return <section className={className || ''}>{renderMonth()}</section>;
};

export default MonthPicker;
